// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import InfoItem from "@/components/InfoItem";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import FileHandleViewer from "@/components/FileHandleViewer";
// Icons
import { Pen, File } from "lucide-react";

// Services and Types
import type { DepartmentDetail } from "@/types/soe";
import formatDate from "@/utils/formatDate";
import { getCompanyDepartmentDetail } from "@/services/soe";
// import { S3_BUCKET_LINK } from "@/components/constants";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/department/$id"
)({
  component: DepartmentDetail,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      data: search.data as string | undefined,
    };
  },
});

function DepartmentDetail() {
  const navigate = useNavigate();
  const params = Route.useParams();
  const { company_management } = useAccessPermission();
  // Query
  const { data: departmentRes, isLoading } = useQuery({
    queryKey: ["departments", params.id],
    queryFn: () => getCompanyDepartmentDetail(params.id),
  });

  if (isLoading) {
    return <Skeleton className="w-full h-[85vh]" />;
  }

  const department = departmentRes?.data as DepartmentDetail;

  return (
    <div className="space-y-[32px] overflow-hidden">
      <div className="text-[18px]">
        <h2 className="font-bold text-zinc-950">ຂໍ້ມູນຄະນະພະແນກ</h2>
        <p className="text-zinc-500">ລາຍລະອຽດຂໍ້ມູນຄະນະພະແນກຂອງວິສາຫະກິດ</p>

        <Separator className="my-6" />
      </div>

      <div className="flex justify-end w-full">
        {company_management.isUpdate && (
          <Button
            onClick={() =>
              navigate({
                to: "/account-management/soe/department/mutate",

                search: {
                  id: department?.id,
                  data: JSON.stringify(department),
                },
              })
            }
          >
            <Pen className="mr-2" />
            ແກ້ໄຂ
          </Button>
        )}
      </div>

      <div className="space-y-[12px] w-full">
        <Table className="rounded-md border">
          <TableHeader className="">
            <TableRow>
              {tableData.headers.map((header) => (
                <TableHead
                  className="font-bold text-black whitespace-nowrap bg-gray-100 border"
                  key={header}
                >
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {department?.companyDeptDetails?.map((item, index) => (
              <TableRow key={item?.id} className="cursor-pointer">
                <TableCell className="whitespace-nowrap border">
                  {index + 1}
                </TableCell>
                <TableCell className="whitespace-nowrap border">
                  <p className="flex gap-2 items-center">{item?.deptName}</p>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap border">
                  {item?.headCount}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap border">
                  {item?.femaleHeadCount}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap border">
                  {item?.empCount}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap border">
                  {item?.femaleEmpCount}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="font-bold text-black bg-gray-100">
              <TableCell colSpan={4} className="text-right border">
                ລວມ
              </TableCell>
              <TableCell className="text-right border">
                {department?.empCount}
              </TableCell>
              <TableCell className="text-right border">
                {department?.femaleEmpCount}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {department?.docFile
        ? company_management.isDownload && (
            <Card className="flex mt-5 border-none">
              <Card className="flex flex-col justify-center mb-4 w-3/12 h-24 bg-gray-100">
                <CardDescription className="p-2 px-4 font-bold text-left text-green-700 text-md">
                  ເອກະສານຕິດຄັດ
                </CardDescription>

                <CardContent className="flex justify-around items-center p-2">
                  {/* <CardDescription className="text-left underline">
                    ເອກະສານຕິດຄັດ
                  </CardDescription>
                  <CardDescription className="flex justify-center items-center w-10 h-10 rounded-md border">
                    <a
                      href={S3_BUCKET_LINK + department?.docFile}
                      download={S3_BUCKET_LINK + department?.docFile}
                    >
                    </a>
                  </CardDescription> */}
                  <FileHandleViewer
                    fileUrl={department?.docFile}
                    showBtnPreview={true}
                  />
                  <File size={24} color="green" />
                </CardContent>
              </Card>
            </Card>
          )
        : null}

      <div className="text-[18px]">
        <h2 className="font-bold text-zinc-950">ຂໍ້ມູນຄະນະພະແນກ</h2>
        <p className="text-zinc-500">ລາຍລະອຽດຂໍ້ມູນຄະນະພະແນກຂອງວິສາຫະກິດ</p>

        <Separator className="my-6" />
      </div>

      <div className="space-y-[12px] w-full">
        <InfoItem
          label="ວັນທີສ້າງ:"
          value={formatDate({
            date: department?.createdAt,
            format: "DD/MM/YYYY",
          })}
        />
        <InfoItem label="ສ້າງໂດຍ:" value={department?.createdBy || "-"} />
        <InfoItem
          label="ອັບເດດລ່າສຸດ:"
          value={formatDate({
            date: department?.updatedAt,
            format: "DD/MM/YYYY",
          })}
        />
        <InfoItem label="ອັບເດດໂດຍ:" value={department?.updatedBy || "-"} />
      </div>
    </div>
  );
}

const tableData = {
  headers: [
    "ລຳດັບ",
    "ຊື່ພະແນກ",
    "ຄະນະພະແນກ (ທັງໝົດ)",
    "ຄະນະພະແນກ (ຍິງ)",
    "ພະນັກງານ (ທັງໝົດ)",
    "ພະນັກງານ (ຍິງ)",
  ],
};
